import { useEffect, useRef, useState } from "react";

import { motion } from "framer-motion";
import { Building2, CreditCard as CreditCardIcon, Lock, Plus } from "lucide-react";

import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import SupportEmailLink from "../../common/SupportEmailLink";
import { Button } from "../../common/customDesignedComponents/button";
import { Checkbox } from "../../common/customDesignedComponents/checkbox";
import AchPaymentPanel, { AchPaymentPanelHandle } from "./AchPaymentPanel";
import { FormInput } from "../../common/customDesignedComponents/FormInput";
import HubspotCookieBreakerInput from "../../common/HubspotCookieBreakerInput";
import StateDropdown from "../../common/customDesignedComponents/StateDropdown";
import { OrderSummaryBreakdown } from "../../common/customDesignedComponents/OrderSummaryModal";
import {
  DEFAULT_URL,
  SERVICE_AGREEMENT_URL,
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
  SUPPORT_PHONE_FORMAT,
} from "../../helpers/constants";
import { getObjLength } from "../../helpers/utils";
import {
  cardToSelect,
  isSavedCardExpired,
  SavedCardList,
} from "./savedCardSelection";

const cardBrandImages: Record<string, string> = {
  visa: "https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg",

  mastercard:
    "https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg",

  amex: "https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg",
};

const stripeElementOptions = {
  style: {
    base: {
      fontSize: "14px",
      color: "#1e293b",
      fontFamily:
        'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSmoothing: "antialiased",
      lineHeight: "44px",
      backgroundColor: "#f9fafb",
      border: "none",
      padding: "0",
      margin: "0",
      height: "44px",
      "::placeholder": {
        color: "#6b7280",
      },
    },
    invalid: {
      color: "#ef4444",
    },
  },
};

type AdminCreateUserPaymentProps = {
  values: Record<string, any>;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean,
  ) => void;
  touched: Record<string, boolean>;
  errors: Record<string, string>;
  validateForm?: () => Promise<Record<string, string> | void>;
  stateOptions: string[];
  stripeErr?: string;
  apiErr?: string;
  checked1: boolean;
  checkTerms: boolean;
  onChecked: (checked: boolean) => void;
  onCardPayment: (data: Record<string, any>) => void;
  onAchPayment: (
    values: Record<string, any>,
    bankAccountId: number | string,
  ) => Promise<void>;
  submitting?: boolean;
  userEmail?: string;
  userId: number | string;
  isServiceProvider?: boolean;
  isResumeSignup?: boolean;
  hideSecureConnectionPanel?: boolean;
  // Card only (no bank account option), e.g. a homeowner buying from a CAR invitation.
  allowAch?: boolean;
  showAdminPricingBreakdown?: boolean;
  pricingSummaryData?: Record<string, unknown> | null;
  selectedCategory?: string;
  showPromoCode?: boolean;
  promoCodeValue?: string;
  onPromoCodeChange?: (value: string) => void;
  onApplyPromoCode?: () => void;
  onRemovePromoCode?: () => void;
  showRemovePromoCode?: boolean;
  promoApplying?: boolean;
  promoMessage?: string;
  promoMessageIsError?: boolean;
  submitLabel?: string;
  totalLabel?: string;
  // C.A.R. invitation checkout: the terms box also covers the Service Agreement (card 3853).
  showServiceAgreement?: boolean;
  // Cards already saved on the payer's account. When one is usable it is
  // offered first; the payment then sends card_id instead of a new token.
  savedCards?: any[];
  defaultCardId?: number | string | null;
  // Shows "Add new card" on the saved-card list; the page opens its Add Card dialog.
  onAddCard?: () => void;
};

const AdminCreateUserPayment = ({
  values,
  setFieldValue,
  setFieldTouched,
  touched,
  errors,
  validateForm,
  stateOptions,
  stripeErr,
  apiErr,
  checked1,
  checkTerms,
  onChecked,
  onCardPayment,
  onAchPayment,
  submitting = false,
  userEmail,
  userId,
  hideSecureConnectionPanel = false,
  allowAch = true,
  isServiceProvider = false,
  isResumeSignup = false,
  showAdminPricingBreakdown = false,
  pricingSummaryData = null,
  selectedCategory = "complete_protection",
  showPromoCode = false,
  promoCodeValue = "",
  onPromoCodeChange,
  onApplyPromoCode,
  onRemovePromoCode,
  showRemovePromoCode = false,
  promoApplying = false,
  promoMessage,
  promoMessageIsError = false,
  submitLabel = "Start My Protection",
  totalLabel = "Total due today",
  showServiceAgreement = false,
  savedCards = [],
  defaultCardId = null,
  onAddCard,
}: AdminCreateUserPaymentProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const achRef = useRef<AchPaymentPanelHandle>(null);
  const [paymentMethod, setPaymentMethod] = useState<"card" | "ach">("card");
  const hasUsableSavedCard = savedCards.some((card) => !isSavedCardExpired(card));
  const [savedCardId, setSavedCardId] = useState<number | null>(null);
  const knownCardIdsRef = useRef<number[] | null>(null);
  // Pick the default (or first unexpired) card once the list arrives; a card
  // added afterwards (Add new card) is picked as soon as it shows up.
  useEffect(() => {
    const ids = savedCards.map((card) => Number(card?.id)).filter(Boolean);
    const known = knownCardIdsRef.current;
    knownCardIdsRef.current = ids;
    const added = known
      ? savedCards.find(
          (card) =>
            !known.includes(Number(card?.id)) && !isSavedCardExpired(card),
        )
      : null;
    if (added) {
      setSavedCardId(added.id);
      return;
    }
    if (savedCardId == null && hasUsableSavedCard) {
      setSavedCardId(cardToSelect(savedCards, defaultCardId)?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUsableSavedCard, savedCards, defaultCardId]);
  // With a usable saved card the homeowner picks one (or adds one through the
  // Add Card dialog); the card form is only for a payer with none.
  const payWithSavedCard = paymentMethod === "card" && hasUsableSavedCard;
  // A saved card needs no billing form when the details on file are complete.
  const billingOnFile = [
    "first_name",
    "last_name",
    "address",
    "city",
    "state",
    "zip",
  ].every((field) => String(values[field] ?? "").trim() !== "");
  const [cardBrand, setCardBrand] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState({
    number: false,
    expiry: false,
    cvc: false,
  });
  const [cardErrors, setCardErrors] = useState({
    number: "",
    expiry: "",
    cvc: "",
  });
  const [validationError, setValidationError] = useState("");
  const [achError, setAchError] = useState("");
  const [processing, setProcessing] = useState(false);
  // Set once the user tries to submit, so an unticked terms box shows its message.
  const [termsSubmitted, setTermsSubmitted] = useState(false);
  const showTermsError = !checkTerms || (termsSubmitted && !checked1);

  // Field ids in on-screen order, used to scroll to the first problem.
  const BILLING_FIELD_IDS: Record<string, string> = {
    first_name: "billing_first_name",
    last_name: "billing_last_name",
    address: "billing_address",
    city: "billing_city",
    state: "billing_state",
    zip: "billing_zip",
  };

  const scrollToFirstError = (ids: string[]) => {
    const target = ids
      .map((id) => document.getElementById(id))
      .find((el): el is HTMLElement => Boolean(el));
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  /**
   * Shows every billing and terms message at once. Returns the ids of the
   * fields with a problem, in on-screen order (empty when all pass).
   */
  const validateBillingFields = async (): Promise<string[]> => {
    const invalidIds: string[] = [];
    if (validateForm) {
      const formErrors = await validateForm();

      if (formErrors && Object.keys(formErrors).length > 0) {
        Object.keys(BILLING_FIELD_IDS).forEach((field) => {
          if (formErrors[field]) invalidIds.push(BILLING_FIELD_IDS[field]);
        });
        Object.keys(formErrors).forEach((field) => {
          setFieldTouched(field, true, false);
        });
      }
    }

    setTermsSubmitted(true);
    if (!checked1) {
      invalidIds.push("admin-payment-terms");
    }
    return invalidIds;
  };

  const blurField = (field: string) => () => setFieldTouched(field, true);

  const CARD_REQUIRED_MESSAGES = {
    number: "Card number is required.",
    expiry: "Expiry date is required.",
    cvc: "CVC is required.",
  } as const;

  // Leaving a card field empty shows its "required" message right away.
  const markCardFieldIfEmpty =
    (field: keyof typeof CARD_REQUIRED_MESSAGES) => () => {
      if (cardComplete[field]) return;
      setCardErrors((prev) =>
        prev[field]
          ? prev
          : { ...prev, [field]: CARD_REQUIRED_MESSAGES[field] },
      );
    };

  const buildPaymentPayload = () => {
    const payload = { ...values };

    if (!payload.referral_code) delete payload.referral_code;

    if (!payload.promo_code) delete payload.promo_code;

    return payload;
  };

  const handleCardSubmit = async () => {
    setValidationError("");

    setAchError("");

    // Check the card, billing fields and terms together so every message
    // shows on one click.
    const nextCardErrors = {
      number: !cardComplete.number
        ? "Card number is required."
        : cardErrors.number,

      expiry: !cardComplete.expiry
        ? "Expiry date is required."
        : cardErrors.expiry,

      cvc: !cardComplete.cvc ? "CVC is required." : cardErrors.cvc,
    };
    setCardErrors(nextCardErrors);
    const cardInvalidIds = [
      nextCardErrors.number ? "card-number-field" : "",
      nextCardErrors.expiry ? "card-expiry-field" : "",
      nextCardErrors.cvc ? "card-cvc-field" : "",
    ].filter(Boolean);

    const billingInvalidIds = await validateBillingFields();
    const invalidIds = [...cardInvalidIds, ...billingInvalidIds];

    if (invalidIds.length > 0) {
      scrollToFirstError(invalidIds);
      return;
    }

    if (!stripe || !elements) {
      setValidationError("Payment system is not ready. Please try again.");

      return;
    }

    setProcessing(true);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);

      if (!cardNumberElement) {
        setValidationError(
          "Unable to process card information. Please try again.",
        );

        return;
      }

      const payload = buildPaymentPayload();
      const result = await stripe.createToken(cardNumberElement, {
        name: `${payload.first_name} ${payload.last_name}`,
        address_line1: payload.address,
        address_city: payload.city,
        address_state: payload.state,
        address_zip: payload.zip,
        address_country: "US",
      });

      if (result.error) {
        setValidationError(result.error.message);

        return;
      }

      onCardPayment({
        ...payload,
        step: 4,
        step_key: "SAVE_CREDIT_CARD",
        token: result.token.id,
        user_id: userId,
      });
    } finally {
      setProcessing(false);
    }
  };

  // Saved card: no Stripe fields to check or tokenize; the payment uses card_id.
  const handleSavedCardSubmit = async () => {
    setValidationError("");
    setAchError("");
    const invalidIds = await validateBillingFields();
    if (invalidIds.length > 0) {
      scrollToFirstError(invalidIds);
      return;
    }
    if (savedCardId == null) {
      setValidationError("Choose a card to pay with.");
      return;
    }
    setProcessing(true);
    try {
      await onCardPayment({
        ...buildPaymentPayload(),
        step: 4,
        step_key: "SAVE_CREDIT_CARD",
        card_id: savedCardId,
        user_id: userId,
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleAchSubmit = async () => {
    setValidationError("");
    setAchError("");
    const invalidIds = await validateBillingFields();
    if (invalidIds.length > 0) {
      scrollToFirstError(invalidIds);
      return;
    }
    setProcessing(true);
    try {
      const linked = await achRef.current?.linkBankAccount();
      if (!linked?.bank_account_id) {
        return;
      }
      await onAchPayment(buildPaymentPayload(), linked.bank_account_id);
    } finally {
      setProcessing(false);
    }
  };

  const isBusy = submitting || processing;

  const billingFields = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
      <div>
        <label className="text-muted-foreground text-xs mb-1 block">
          First Name
        </label>

        <FormInput
          name="first_name"
          id="billing_first_name"
          autoComplete="billing given-name"
          value={values.first_name || ""}
          onChange={(e) => setFieldValue("first_name", e.target.value)}
          touched={Boolean(touched.first_name)}
          error={errors.first_name}
          onBlur={blurField("first_name")}
        />
      </div>

      <div>
        <label className="text-muted-foreground text-xs mb-1 block">
          Last Name
        </label>

        <FormInput
          name="last_name"
          id="billing_last_name"
          autoComplete="billing family-name"
          value={values.last_name || ""}
          onChange={(e) => setFieldValue("last_name", e.target.value)}
          touched={Boolean(touched.last_name)}
          error={errors.last_name}
          onBlur={blurField("last_name")}
        />
      </div>

      <div>
        <label className="text-muted-foreground text-xs mb-1 block">
          Address
        </label>

        <FormInput
          name="address"
          id="billing_address"
          autoComplete="billing address-line1"
          value={values.address || ""}
          onChange={(e) => setFieldValue("address", e.target.value)}
          touched={Boolean(touched.address)}
          error={errors.address}
          onBlur={blurField("address")}
        />
      </div>

      <div>
        <label className="text-muted-foreground text-xs mb-1 block">City</label>

        <FormInput
          name="city"
          id="billing_city"
          autoComplete="billing address-level2"
          value={values.city || ""}
          onChange={(e) => setFieldValue("city", e.target.value)}
          touched={Boolean(touched.city)}
          error={errors.city}
          onBlur={blurField("city")}
        />
      </div>

      <div id="billing_state">
        <StateDropdown
          stateList={stateOptions.map((state) => ({
            value: state,

            label: state,
          }))}
          value={values.state || ""}
          onChange={(value) => setFieldValue("state", value)}
          onBlur={blurField("state")}
          error={errors.state}
          touched={Boolean(touched.state)}
        />
      </div>

      <div>
        <label className="text-muted-foreground text-xs mb-1 block">Zip</label>

        <FormInput
          name="zip"
          id="billing_zip"
          autoComplete="billing postal-code"
          value={values.zip || ""}
          onChange={(e) =>
            setFieldValue("zip", e.target.value.replace(/\D/g, "").slice(0, 5))
          }
          touched={Boolean(touched.zip)}
          error={errors.zip}
          onBlur={blurField("zip")}
        />
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="tailwind_component max-w-3xl mx-auto"
    >
      <div
        className={`bg-card  ${!isResumeSignup && "border border-border shadow-lg rounded-2xl"}`}
      >
        {!isResumeSignup && !hideSecureConnectionPanel && (
          <div className="bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2 rounded-t-2xl overflow-hidden">
            <Lock className="h-4 w-4 text-white" />

            <span className="text-white font-medium text-sm">
              Secure connection
            </span>
          </div>
        )}

        <div className="p-6 md:p-8">
          <h1 className="text-xl font-display font-medium text-foreground text-center mb-6">
            Payment details
          </h1>

          <HubspotCookieBreakerInput
            userEmail={userEmail}
            isPropertyForm={true}
          />

          {allowAch ? (
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                type="button"
                onClick={() => setPaymentMethod("card")}
                className={`flex items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${
                  paymentMethod === "card"
                    ? "border-secondary bg-secondary/10 text-secondary"
                    : "border-border text-muted-foreground hover:border-secondary/40"
                }`}
              >
                <CreditCardIcon className="h-4 w-4" />
                Credit card
              </button>

              <button
                type="button"
                onClick={() => setPaymentMethod("ach")}
                className={`flex items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${
                  paymentMethod === "ach"
                    ? "border-secondary bg-secondary/10 text-secondary"
                    : "border-border text-muted-foreground hover:border-secondary/40"
                }`}
              >
                <Building2 className="h-4 w-4" />
                Bank account (ACH)
              </button>
            </div>
          ) : null}

          {payWithSavedCard ? (
            <div className="mb-6">
              <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium text-foreground">
                  Pay with a saved card
                </p>
                {onAddCard ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-secondary border-secondary hover:bg-secondary/10 shrink-0"
                    onClick={onAddCard}
                  >
                    <Plus className="h-4 w-4 mr-1.5" />
                    Add new card
                  </Button>
                ) : null}
              </div>
              <SavedCardList
                cards={savedCards}
                selectedCardId={savedCardId}
                defaultCardId={defaultCardId}
                onSelect={(card: any) => setSavedCardId(card.id)}
                className="space-y-3"
              />
            </div>
          ) : null}

          {paymentMethod === "card" && !payWithSavedCard && (
            <>
              <div className="mb-3" id="card-number-field">
                <label className="text-muted-foreground text-xs mb-1 block">
                  Card number
                </label>

                <div
                  className={`flex items-center border rounded-lg bg-muted/30 overflow-hidden h-11 ${
                    cardErrors.number ? "card_errors" : "border-border"
                  }`}
                >
                  <div className="flex-1 px-3 h-11 min-w-0 flex items-center">
                    <CardNumberElement
                      className="stripe-card-number w-full"
                      options={stripeElementOptions}
                      onBlur={markCardFieldIfEmpty("number")}
                      onChange={(event) => {
                        if (event.brand) setCardBrand(event.brand);

                        setCardComplete((prev) => ({
                          ...prev,

                          number: event.complete,
                        }));

                        setCardErrors((prev) => ({
                          ...prev,

                          number: event.error ? event.error.message : "",
                        }));
                      }}
                    />
                  </div>

                  <div className="flex items-center gap-1.5 px-3 shrink-0 border-l border-border/50 h-11">
                    {cardBrand && cardBrandImages[cardBrand] ? (
                      <img
                        src={cardBrandImages[cardBrand]}
                        alt={cardBrand}
                        className="h-5 w-auto object-contain"
                      />
                    ) : (
                      <>
                        <img
                          src={cardBrandImages.visa}
                          alt="Visa"
                          className="h-5 w-auto object-contain"
                        />

                        <img
                          src={cardBrandImages.mastercard}
                          alt="Mastercard"
                          className="h-5 w-auto object-contain"
                        />
                      </>
                    )}
                  </div>
                </div>

                {cardErrors.number && (
                  <p className="text-destructive text-xs mt-1">
                    {cardErrors.number}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div id="card-expiry-field">
                  <label className="text-muted-foreground text-xs mb-1 block">
                    Expiration
                  </label>

                  <div
                    className={`border rounded-lg bg-muted/30 h-11 flex items-center overflow-hidden ${
                      cardErrors.expiry ? "card_errors" : "border-border"
                    }`}
                  >
                    <div className="px-3 h-11 flex items-center w-full">
                      <CardExpiryElement
                        className="stripe-card-expiry"
                        options={stripeElementOptions}
                        onBlur={markCardFieldIfEmpty("expiry")}
                        onChange={(event) => {
                          setCardComplete((prev) => ({
                            ...prev,

                            expiry: event.complete,
                          }));

                          setCardErrors((prev) => ({
                            ...prev,

                            expiry: event.error ? event.error.message : "",
                          }));
                        }}
                      />
                    </div>
                  </div>

                  {cardErrors.expiry && (
                    <p className="text-destructive text-xs mt-1">
                      {cardErrors.expiry}
                    </p>
                  )}
                </div>

                <div id="card-cvc-field">
                  <label className="text-muted-foreground text-xs mb-1 block">
                    CVC
                  </label>

                  <div
                    className={`relative border rounded-lg bg-muted/30 h-11 flex items-center overflow-hidden ${
                      cardErrors.cvc ? "card_errors" : "border-border"
                    }`}
                  >
                    <div className="px-3 pr-10 h-11 flex items-center w-full">
                      <CardCvcElement
                        className="stripe-card-cvc"
                        options={stripeElementOptions}
                        onBlur={markCardFieldIfEmpty("cvc")}
                        onChange={(event) => {
                          setCardComplete((prev) => ({
                            ...prev,

                            cvc: event.complete,
                          }));

                          setCardErrors((prev) => ({
                            ...prev,

                            cvc: event.error ? event.error.message : "",
                          }));
                        }}
                      />
                    </div>

                    <CreditCardIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none shrink-0" />
                  </div>

                  {cardErrors.cvc && (
                    <p className="text-destructive text-xs mt-1">
                      {cardErrors.cvc}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* A saved card uses the billing details on file unless some are missing. */}
          {payWithSavedCard && billingOnFile ? null : billingFields}

          {paymentMethod === "ach" && (
            <div className="mb-8 pb-2">
              <AchPaymentPanel
                ref={achRef}
                userId={userId}
                isServiceProvider={isServiceProvider}
                onError={(message) => setAchError(message)}
              />

              {achError && (
                <p className="text-destructive text-xs mt-2">{achError}</p>
              )}
            </div>
          )}

          {(validationError || stripeErr || apiErr) && (
            <p className="text-destructive text-sm text-center mb-4">
              {validationError || stripeErr || apiErr}
            </p>
          )}

          <div
            className={`rounded-lg border p-4 mb-6 ${
              showTermsError
                ? "border-destructive/40 bg-destructive/5"
                : "border-border"
            }`}
          >
            <div className="flex items-start gap-3">
              <Checkbox
                checked={checked1}
                onCheckedChange={(checked) => onChecked(Boolean(checked))}
                id="admin-payment-terms"
              />

              <label
                htmlFor="admin-payment-terms"
                className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
              >
                By checking this box, you are agreeing to the terms below,
                including EquityProtect&apos;s Subscription and Automatic
                Renewal Payment Authorization terms and{" "}
                <a
                  href={`${DEFAULT_URL}terms`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-secondary underline"
                >
                  Terms and Conditions
                </a>{" "}
                of Use
                {showServiceAgreement ? (
                  <>
                    {" "}and the{" "}
                    {SERVICE_AGREEMENT_URL ? (
                      <a
                        href={SERVICE_AGREEMENT_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="text-secondary underline"
                      >
                        Service Agreement
                      </a>
                    ) : (
                      "Service Agreement"
                    )}
                  </>
                ) : null}
                .
              </label>
            </div>

            {showTermsError && (
              <p className="text-destructive text-xs mt-2">
                Please accept the terms to proceed.
              </p>
            )}

            <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
              Subscription & Automatic Renewal Payment Authorization. The
              subscription begins when your purchase is completed, and it will
              automatically renew before each renewal period (plus applicable
              state taxes). By subscribing, you authorize us to automatically
              charge the selected payment method before each renewal, unless you
              cancel through your account dashboard, contact{" "}
              <SupportEmailLink
                email={SUPPORT_EMAIL}
                text={SUPPORT_EMAIL}
                className="text-secondary underline"
              />
              , or call{" "}
              <a
                href={`tel:${SUPPORT_PHONE}`}
                className="text-secondary underline"
              >
                {SUPPORT_PHONE_FORMAT}
              </a>
              .
            </p>
          </div>

          {showAdminPricingBreakdown &&
          pricingSummaryData &&
          getObjLength(pricingSummaryData) ? (
            <OrderSummaryBreakdown
              paymentData={pricingSummaryData as any}
              category={selectedCategory}
              totalLabel={totalLabel}
              className="mb-4"
              showPromoCode={showPromoCode}
              promoCodeValue={promoCodeValue}
              onPromoCodeChange={onPromoCodeChange}
              onApplyPromoCode={onApplyPromoCode}
              onRemovePromoCode={onRemovePromoCode}
              showRemovePromoCode={showRemovePromoCode}
              promoApplying={promoApplying}
              promoMessage={promoMessage}
              promoMessageIsError={promoMessageIsError}
            />
          ) : null}

          <div className="flex justify-center">
            <Button
              type="button"
              disabled={
                isBusy ||
                (paymentMethod === "card" && !payWithSavedCard && !stripe)
              }
              onClick={
                paymentMethod !== "card"
                  ? handleAchSubmit
                  : payWithSavedCard
                    ? handleSavedCardSubmit
                    : handleCardSubmit
              }
              className="bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 text-white font-semibold px-12 py-3 text-sm rounded-full shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wide"
            >
              {isBusy ? "Processing…" : submitLabel}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AdminCreateUserPayment;
